import * as yup from 'yup';

import {
  isDigits,
  numberSchema,
  requiredNumber,
  requiredString,
  requiredValidRange,
  timeSchema,
  validRangeWithDecimal,
} from '../../../../common/validation/yupUtils';
import { referenceFrequencySchemaWithEmissions } from '../../../reference_data/spectrum_record/validationSchema';

const antennaHeightSchema = numberSchema
  .integer('Must be an integer')
  .min(-999, 'Must be in the range -999 to 999')
  .max(999, 'Must be in the range -999 to 999')
  .required('Required');

const antennaHeightSaveSchema = numberSchema
  .integer('Must be an integer')
  .min(-999, 'Must be in the range -999 to 999')
  .max(999, 'Must be in the range -999 to 999')
  .notRequired();

const configurationLossSchema = validRangeWithDecimal(0, 999.9, 1, 'Must be one decimal place and in the range 0 to 999.9');

const satelliteTransmitLocationSchema = yup.object().shape({
  id: requiredNumber,
  antennaHeight: antennaHeightSaveSchema,
  configurationLoss: configurationLossSchema,
});

const satelliteReceiveLocationSchema = yup.object().shape({
  id: requiredNumber,
  noiseTemperature: numberSchema
    .notRequired()
    .min(50, 'Must be Kelvin units in the range 50 to 10000')
    .max(10000, 'Must be Kelvin units in the range 50 to 10000')
    .test('digits', 'Please enter a digit number, decimal are not allowed', (value) => value == null || isDigits('' + value)),
});

export const satelliteTransmitLocationsSchema = yup.object().shape({
  locations: yup.array().nullable(true).of(satelliteTransmitLocationSchema),
});

export const getSatelliteReceiveLocationsSchema = (classification?: string) => {
  const receiveLocationSchemaWithClassification =
    classification === 'C'
      ? satelliteReceiveLocationSchema.shape({
          antennaHeight: antennaHeightSaveSchema,
          configurationLoss: configurationLossSchema,
        })
      : satelliteReceiveLocationSchema;

  return yup.object().shape({
    locations: yup.array().nullable(true).of(receiveLocationSchemaWithClassification),
  });
};

const minValidationFrequency = yup.object().shape(
  {
    referenceFrequency: yup
      .number()
      .nullable(true)
      .when(['referenceFrequencyType', 'power', 'powerType'], {
        is: (referenceFrequencyType, power, powerType) => !!referenceFrequencyType || !!power || !!powerType,
        then: requiredNumber,
      }),
    referenceFrequencyType: yup
      .string()
      .nullable(true)
      .when(['referenceFrequency', 'power', 'powerType'], {
        is: (referenceFrequency, power, powerType) => !!referenceFrequency || !!power || !!powerType,
        then: requiredString,
      }),
    power: yup
      .number()
      .nullable(true)
      .when(['referenceFrequencyType', 'referenceFrequency', 'powerType'], {
        is: (referenceFrequencyType, referenceFrequency, powerType) => !!referenceFrequencyType || !!referenceFrequency || !!powerType,
        then: requiredNumber,
      }),
    powerType: yup
      .string()
      .nullable(true)
      .when(['referenceFrequencyType', 'power', 'referenceFrequency'], {
        is: (referenceFrequencyType, power, referenceFrequency) => !!referenceFrequencyType || !!power || !!referenceFrequency,
        then: requiredString,
      }),
    isoStartTime: timeSchema,
    isoStopTime: timeSchema,
  },
  [
    ['referenceFrequency', 'referenceFrequencyType'],
    ['referenceFrequency', 'power'],
    ['referenceFrequency', 'powerType'],
    ['referenceFrequencyType', 'power'],
    ['referenceFrequencyType', 'powerType'],
    ['power', 'powerType'],
  ],
);

export const satelliteSpectrumDetailsSchema = yup.object().shape({
  spectrum: yup.object().shape({
    lowerBound: requiredNumber,
    upperBound: requiredNumber,
    frequencies: yup.array().nullable(true).of(minValidationFrequency),
  }),
});

export const spectrumDoneSchema = yup.object().shape({
  lowerBound: requiredNumber,
  upperBound: requiredNumber,
  polarisation: requiredString,
  frequencies: referenceFrequencySchemaWithEmissions,
});

export const satelliteSpectrumsDoneSchema = yup.array().of(spectrumDoneSchema);

const locationCommonDoneSchema = yup.object().shape({
  id: requiredNumber,
  stationType: requiredString,
});

/* Transmit location is input for 'EARTH-TO-SPACE' and 'EARTH-TO-SPACE and SPACE-TO-EARTH'  satellite licences */
export const transmitLocationDoneSchema = locationCommonDoneSchema.shape({
  equipment: yup.object().nullable(true).required('Required'),
  antennaHeight: antennaHeightSchema,
  configurationLoss: configurationLossSchema,
  antenna: yup.object().nullable(true).required('Required'),
  antennaPattern: requiredString,
  antennaPatternOther: yup
    .string()
    .nullable(true)
    .when(['antennaPattern'], {
      is: (pattern: string) => pattern === 'Other',
      then: requiredString,
    }),
});

/* Receive location is input for 'SPACE-TO-EARTH' and 'EARTH-TO-SPACE and SPACE-TO-EARTH' satellite licences */
export const receiveLocationDoneSchema = locationCommonDoneSchema
  .shape({
    noiseTemperature: requiredValidRange(50, 10000, 'Must be Kelvin units in the range 50 to 10000').test(
      'digits',
      'Please enter a digit number, decimal are not allowed',
      (value) => isDigits('' + value),
    ),
  })
  .when('$context', (contextObj, schema) => {
    /* Logic Check:
        1. contextObj?.classification -> Handles the manual isValidSync call, from renderRowAsError in LocationSection.tsx
                                      -> Handles call from validateSatelliteReceiveLocation in DoneValidationHooks.tsx
        2. contextObj?.context?.classification -> Handles the validateWithFormValues call from <Formik> validate in LocationSection.tsx
     */
    const classification = contextObj?.classification || contextObj?.context?.classification;
    if (classification === 'A') {
      return schema.shape({ stationType: yup.string().nullable(true).notRequired() });
    }
    /* SPACE-TO-EARTH has more Receive Location fields to validate than SPACE-TO-EARTH and EARTH-TO-SPACE */
    if (classification === 'C') {
      return schema.shape({
        equipment: yup.object().nullable(true).required('Required'),
        antennaHeight: antennaHeightSchema,
        configurationLoss: configurationLossSchema,
        antenna: yup.object().nullable(true).required('Required'),
        antennaPattern: requiredString,
        antennaPatternOther: yup
          .string()
          .nullable(true)
          .when(['antennaPattern'], {
            is: (pattern: string) => pattern === 'Other',
            then: requiredString,
          }),
      });
    }
    return schema;
  });

export const satelliteTransmitLocationsDoneSchema = yup.object().shape({
  locations: yup.array().nullable(true).of(transmitLocationDoneSchema),
});

export const satelliteReceiveLocationsDoneSchema = yup.object().shape({
  locations: yup.array().nullable(true).of(receiveLocationDoneSchema),
});

export const satelliteTransmitLocationAllDoneSchema = yup.object().shape({
  spectrum: spectrumDoneSchema,
  locations: yup.array(transmitLocationDoneSchema),
});

export const satelliteReceiveLocationAllDoneSchema = yup.object().shape({
  spectrum: spectrumDoneSchema,
  locations: yup.array(receiveLocationDoneSchema),
});
